import { ClipboardList, Lock } from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";

import { Hint } from "@/components/ui/hint";
import { useOrgHref } from "@/hooks/useOrgParamSync";
import { cn } from "@/lib/utils";

/**
 * The Desk launcher — a control on the rail, not a destination in it.
 *
 * Desk is its own application rather than another page of this console
 * (spec §8.1): putting it in the main nav would have made it the tenth
 * reporting surface in a list of reporting surfaces, and a technician who
 * lives in Desk all day would enter it through the product they are *not*
 * using. So it sits beside Settings and the currency chip, in the part of the
 * rail that holds controls rather than destinations.
 *
 * **It navigates in place.** It opened in a new tab until 2026-09-11, on the
 * argument that a separate application deserves a separate window. In use that
 * was wrong twice over: every click from the console spawned another tab, and
 * the tabs accumulated because nothing ever closed them. Being a distinct
 * application is not a reason to take the browser out of the user's hands —
 * anyone who wants Desk in its own window can still middle-click, which a
 * router link supports and a forced `target="_blank"` takes away.
 */
export function SidebarDesk({
  collapsed,
  compact = false,
  locked,
}: {
  collapsed: boolean;
  /** Share a row rather than occupying one, as the currency chip does. */
  compact?: boolean;
  /**
   * Why this person cannot open Desk, when they cannot. Marked, not removed,
   * the same convention as the Billing row: the product keeps its shape and
   * the reason is on hover.
   */
  locked?: string;
}) {
  const { pathname } = useLocation();
  // The scope bar's selection rides on the link, as on every sidebar row.
  const carry = useOrgHref();
  if (locked) {
    return (
      // Focusable so the reason is reachable from the keyboard, not only on
      // hover; it is read out as the row's description.
      <Hint content={locked} side="right">
        <div
          tabIndex={0}
          aria-disabled="true"
          aria-label="Desk"
          className={cn(
            "group flex cursor-not-allowed items-center rounded-md text-sm font-medium text-sidebar-muted opacity-45",
            collapsed
              ? "w-full justify-center py-2.5"
              : compact
                ? "shrink-0 gap-1.5 border border-sidebar-border/60 px-2 py-2"
                : "w-full gap-3 px-3 py-2.5",
          )}
        >
          <ClipboardList className="h-[18px] w-[18px] shrink-0" aria-hidden />
          {!collapsed && !compact && (
            <>
              <span className="flex-1 text-left">Desk</span>
              <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden />
            </>
          )}
        </div>
      </Hint>
    );
  }
  // `startsWith` rather than equality: a deep-linked ticket (`/desk/tickets/4`)
  // is still Desk, and the rail should say so.
  const isActive = pathname === "/desk" || pathname.startsWith("/desk/");

  return (
    // Folded, the launcher is a bare icon, so its name is the hint beside it.
    // The className below is a plain string, which is what lets NavLink sit
    // under the hint's trigger slot.
    <Hint content={collapsed ? "Desk" : undefined} side="right">
      <NavLink
        to={carry("/desk")}
        aria-current={isActive ? "page" : undefined}
        aria-label="Desk"
        className={cn(
          "group flex items-center rounded-md text-sm transition-colors duration-200",
          collapsed
            ? "w-full justify-center py-2.5"
            : compact
              ? // The hairline border separates two adjacent controls on one row;
                // without it the icon reads as trailing decoration belonging to
                // whatever sits to its left.
                "shrink-0 gap-1.5 border border-sidebar-border/60 px-2 py-2"
              : "w-full gap-3 px-3 py-2.5",
          isActive
            ? "bg-primary/10 font-semibold text-primary"
            : "font-medium text-sidebar-muted hover:bg-sidebar-accent hover:text-sidebar-foreground",
        )}
      >
        <ClipboardList className="h-[18px] w-[18px] shrink-0" aria-hidden />
        {!collapsed && !compact && (
          <span className="flex-1 text-left">Desk</span>
        )}
      </NavLink>
    </Hint>
  );
}
